import { useEffect, useState } from 'react';
import { Alert, Anchor, Badge, Button, Card, Checkbox, Group, Text, Title } from '@mantine/core';
import { api } from '../api/client';
import type { Interaction } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { whenLabel } from '../format';
import { approvalOf, isAiDraft, modelLabel, modelOf } from '../interaction/provenance';
import { ErrorNote } from './ErrorNote';

/**
 * Everything the editor shows about an interaction a model wrote.
 *
 * There are two pieces and they are deliberately far apart on the page. The badge and the line
 * under the heading say where this came from, at a glance, before anybody reads a word of it. The
 * approval itself sits at the foot of the questions, because approving is a statement that
 * somebody has read them, and a button above the fold is a button people press on their way past.
 *
 * A human written interaction gets none of this. Approval furniture on something nobody needs to
 * approve would teach editors to click through it, which is exactly what would make the real one
 * worthless.
 */

/** Shown beside the status, so provenance is visible before anything is read. */
export function ProvenanceBadge({ interaction }: { interaction: Interaction }) {
  if (!isAiDraft(interaction)) return null;
  return (
    <Badge size="sm" radius="sm" variant="light" color="gray">
      AI draft
    </Badge>
  );
}

/** Which model wrote it, where from, and whether anybody has stood behind it yet. */
export function ProvenanceNote({ interaction, name }: { interaction: Interaction; name: string }) {
  const { org } = useAuth();
  const approval = approvalOf(interaction);
  const approver = useApproverName(org?.org_id ?? null, approval === null ? null : approval.by);
  const model = modelOf(interaction);
  const sourceUrl = interaction.provenance?.source_url;

  if (!isAiDraft(interaction)) return null;

  return (
    <>
      <Text c="dimmed" size="sm" mt="xs">
        Drafted by {model === null ? 'a model' : modelLabel(model)}{' '}
        {sourceUrl === undefined ? 'from a description somebody typed.' : 'from one of your pages.'}{' '}
        {approval === null
          ? `Nobody has approved it yet, so this ${name} cannot go live. The approve button is below the questions.`
          : approvedSentence(approver, approval.at)}
      </Text>
      {sourceUrl !== undefined && (
        <Text c="dimmed" size="sm">
          Read from{' '}
          <Anchor href={sourceUrl} target="_blank" rel="noreferrer" size="sm">
            {sourceUrl}
          </Anchor>
          . Check what it says against the page: a model can misread a story as easily as it can
          invent one.
        </Text>
      )}
    </>
  );
}

/**
 * The decision itself.
 *
 * The tick box is not ceremony. The server will not publish an AI draft without an approval, and
 * that refusal is worth nothing if approving is one click from arriving on the page, so approving
 * asks somebody to say they have read it. Once approved, the panel keeps saying what approval
 * covers, because editing afterwards is allowed and nothing will ask a second time.
 */
export function ApprovalPanel({
  interaction,
  name,
  dirty,
  approving,
  problem,
  onApprove,
}: {
  interaction: Interaction;
  name: string;
  /** True while there are edits on screen that have not been saved. */
  dirty: boolean;
  approving: boolean;
  problem: string | null;
  onApprove: () => void;
}) {
  const { org } = useAuth();
  const [read, setRead] = useState(false);
  const approval = approvalOf(interaction);
  const approver = useApproverName(org?.org_id ?? null, approval === null ? null : approval.by);
  const model = modelOf(interaction);

  if (!isAiDraft(interaction)) return null;

  if (approval !== null) {
    return (
      <Card
        component="section"
        withBorder
        radius="lg"
        p="lg"
        mt="xl"
        aria-labelledby="approval-heading"
      >
        <Title order={2} size="h3" id="approval-heading">
          Approved
        </Title>
        <Text mt="xs">{approvedSentence(approver, approval.at)}</Text>
        <Text c="dimmed" size="sm" mt="xs">
          That approval covers what is on this screen now, including anything you change from here.
          Nothing will ask you again, so read your changes before you publish.
        </Text>
        {dirty && (
          <Alert role="alert" color="yellow" variant="light" radius="md" mt="md">
            <Text size="sm">
              You have edited this {name} since it was approved. Save it and read it through:
              publishing will not stop to ask.
            </Text>
          </Alert>
        )}
      </Card>
    );
  }

  return (
    <Card
      component="section"
      withBorder
      radius="lg"
      p="lg"
      mt="xl"
      aria-labelledby="approval-heading"
    >
      <Title order={2} size="h3" id="approval-heading">
        Approve this draft
      </Title>
      <Text mt="xs">
        {model === null ? 'A model' : modelLabel(model)} wrote this {name}. It cannot be published
        until somebody has read it and approved it.
      </Text>
      <Text c="dimmed" size="sm" mt="xs">
        Read every question and every answer above. Check the facts, the names and the tone, and
        change anything that is not right. Approving puts your name to these words.
      </Text>

      {problem !== null && <ErrorNote message={problem} />}

      <Checkbox
        mt="md"
        checked={read}
        onChange={(event) => setRead(event.currentTarget.checked)}
        disabled={approving}
        label="I have read this draft and I am happy for readers to see it"
      />

      <Group mt="md" gap="md" align="center">
        {/* Explicitly not a submit: this panel sits inside the editor's form, and approving is
            not saving. */}
        <Button type="button" onClick={onApprove} disabled={!read || approving}>
          {approving ? 'Approving...' : 'Approve this draft'}
        </Button>
        {!read && (
          <Text size="sm" c="dimmed">
            Tick the box once you have read it through.
          </Text>
        )}
      </Group>
    </Card>
  );
}

function approvedSentence(approver: string | null, at: string | null): string {
  const who = approver === null ? '' : ` by ${approver}`;
  const when = at === null ? '' : ` on ${whenLabel(at)}`;
  return `Approved${who}${when}.`;
}

/**
 * The name behind an approver's user id.
 *
 * The approval carries an id, which is the right thing to store and the wrong thing to read, so
 * the org's members are asked for the name. Whoever has just approved something is almost always
 * the person looking at the screen, and that case needs no request at all. If the lookup fails the
 * sentence simply loses the name: the date is the fact that matters and it is already there.
 */
function useApproverName(orgId: string | null, userId: string | null): string | null {
  const { user } = useAuth();
  const [name, setName] = useState<string | null>(null);

  useEffect(() => {
    setName(null);
    if (orgId === null || userId === null) return;
    if (user !== null && user.user_id === userId) {
      setName('you');
      return;
    }

    let cancelled = false;
    api
      .members(orgId)
      .then((body) => {
        if (cancelled) return;
        setName(body.members.find((member) => member.user_id === userId)?.name ?? null);
      })
      .catch(() => {
        // No name, then. Not worth an error message on a screen about something else.
      });
    return () => {
      cancelled = true;
    };
  }, [orgId, user, userId]);

  return name;
}
